import { useCallback, useEffect, useState } from "react";

import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { Button, Grid, IconButton, Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "@/features/authentication";
import { UserOutletsSelect } from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import {
  apiOutletClient,
  formatDate,
  getErrorMessage,
} from "@/features/shared";
import {
  CustomDateRangePicker,
  SearchTextField,
  useSnackbar,
} from "@/features/ui";
import {
  useDateRangeFilter,
  useSearchFilter,
  useServerPagination,
} from "@/hooks";

export const StockAdjustments = () => {
  const { outlet, changeOutlet, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [stockAdjustments, setStockAdjustments] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });
  const { dateRangeProps, dateParams } = useDateRangeFilter({ resetParams });

  const loadStockAdjustments = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiOutletClient.get("stock-adjustments", {
        params: {
          ...paginationParams,
          ...dateParams,
          ...(keyword && { keyword: keyword }),
          withWarehouse: true,
        },
      });
      setStockAdjustments(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat penyesuaian stok"));
    } finally {
      setLoading(false);
    }
  }, [
    outlet,
    paginationParams,
    dateParams,
    keyword,
    setRowCount,
    showSnackbar,
  ]);

  useEffect(() => {
    loadStockAdjustments();
  }, [loadStockAdjustments]);

  const toolbarContent = hasPermission(
    PermissionEnum.CREATE_STOCK_ADJUSTMENTS
  ) && (
    <Button fullWidth variant="contained" component={Link} to="create">
      Tambah
    </Button>
  );

  return (
    <PageContainer
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
      maxWidth={false}
    >
      <Stack spacing={3}>
        <Grid container spacing={2} sx={{ justifyContent: "flex-end", alignItems: "end" }}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={changeOutlet}
              TextFieldProps={{ label: "Outlet" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari No. Referensi"
              {...searchProps}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 6 }}>
            <CustomDateRangePicker {...dateRangeProps} />
          </Grid>
        </Grid>
        <DataGrid
          rows={stockAdjustments}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "referenceNumber",
              headerName: "Nomor Referensi",
              flex: 1.5,
              sortable: false,
            },
            {
              field: "transactionDate",
              headerName: "Tanggal Transaksi",
              flex: 1.5,
              sortable: false,
              valueGetter: (transactionDate) => formatDate(transactionDate),
            },
            {
              field: "warehouse",
              headerName: "Gudang",
              flex: 1.5,
              sortable: false,
              valueGetter: (warehouse) => warehouse?.name || "-",
            },
            {
              field: "note",
              headerName: "Catatan",
              flex: 2,
              sortable: false,
              valueGetter: (note) => note ?? "-",
            },
            {
              field: "actions",
              type: "actions",
              headerName: "Aksi",
              getActions: ({ row }) => {
                const actions = [];
                if (hasPermission(PermissionEnum.READ_STOCK_ADJUSTMENTS)) {
                  actions.push(
                    <IconButton
                      onClick={() => navigate(row.referenceNumber)}
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  );
                }
                return actions;
              },
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
